import { marked } from 'marked';
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import python from 'highlight.js/lib/languages/python';
import java from 'highlight.js/lib/languages/java';
import bash from 'highlight.js/lib/languages/bash';
import json from 'highlight.js/lib/languages/json';
import xml from 'highlight.js/lib/languages/xml';
import css from 'highlight.js/lib/languages/css';
import sql from 'highlight.js/lib/languages/sql';
import yaml from 'highlight.js/lib/languages/yaml';
import kotlin from 'highlight.js/lib/languages/kotlin';
import plaintext from 'highlight.js/lib/languages/plaintext';

hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('java', java);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('shell', bash);
hljs.registerLanguage('json', json);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('css', css);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);
hljs.registerLanguage('kotlin', kotlin);
hljs.registerLanguage('plaintext', plaintext);

marked.use({
  gfm: true,
  renderer: {
    heading(token) {
      const text = this.parser.parseInline(token.tokens);
      return `<h${token.depth} class="md-h md-h${token.depth}">${text}</h${token.depth}>\n`;
    },
    paragraph(token) {
      const text = this.parser.parseInline(token.tokens);
      return `<p class="md-p">${text}</p>\n`;
    },
    code(token) {
      const lang = (token.lang || '').split(/\s+/)[0];
      const language = hljs.getLanguage(lang) ? lang : 'plaintext';
      const highlighted = hljs.highlight(token.text, { language }).value;
      return `<pre class="md-code"><code class="hljs language-${language}">${highlighted}</code></pre>\n`;
    },
    codespan(token) {
      return `<code class="md-icode">${token.text}</code>`;
    },
    blockquote(token) {
      const text = this.parser.parse(token.tokens);
      return `<blockquote class="md-quote">${text}</blockquote>\n`;
    },
    hr() {
      return '<hr class="md-hr"/>\n';
    },
    link(token) {
      const text = this.parser.parseInline(token.tokens);
      return `<a href="${token.href}" target="_blank" rel="noreferrer">${text}</a>`;
    },
    list(token) {
      const tag = token.ordered ? 'ol' : 'ul';
      const body = token.items.map(item => this.listitem(item)).join('');
      return `<${tag} class="md-list">${body}</${tag}>\n`;
    },
    listitem(token) {
      if (token.task) {
        const ck = token.checked ? 'is-on' : '';
        const text = token.tokens
          .filter(t => t.type !== 'checkbox')
          .map(t => this.parser.parseInline(t.tokens || []))
          .join('');
        return `<li class="md-task"><span class="md-checkbox ${ck}"></span>${text}</li>\n`;
      }
      const text = this.parser.parse(token.tokens);
      return `<li>${text}</li>\n`;
    },
    table(token) {
      const heads = token.header.map(cell => {
        const text = this.parser.parseInline(cell.tokens);
        const align = cell.align ? ` style="text-align:${cell.align}"` : '';
        return `<th${align}>${text}</th>`;
      }).join('');
      const rows = token.rows.map(row =>
        '<tr>' + row.map(cell => {
          const text = this.parser.parseInline(cell.tokens);
          const align = cell.align ? ` style="text-align:${cell.align}"` : '';
          return `<td${align}>${text}</td>`;
        }).join('') + '</tr>'
      ).join('\n');
      return `<div class="md-table-wrap"><table class="md-table"><thead><tr>${heads}</tr></thead><tbody>${rows}</tbody></table></div>\n`;
    },
  },
});

export function renderMarkdown(md) {
  if (!md) return '';
  return String(marked.parse(md));
}

export const MD_STYLES = (p, m) => `
.md-body{font-size:14.5px;line-height:1.75;color:#2a2a3a}
.md-h{margin:28px 0 10px;letter-spacing:-0.01em;color:#1f1d2e;font-weight:700;line-height:1.3}
.md-h1{font-size:26px;padding-bottom:10px;border-bottom:1px solid #ececf2;margin-top:0}
.md-h2{font-size:20px}.md-h3{font-size:17px}.md-h4{font-size:15px}
.md-p{margin:10px 0}
.md-icode{background:#f4f3f9;padding:2px 6px;border-radius:4px;font-size:13px;color:${p};font-family:ui-monospace,monospace}
.md-code{background:#1f1d2e;color:#e6e5f0;padding:14px 16px;border-radius:10px;margin:14px 0;font-size:13px;line-height:1.6;overflow-x:auto;font-family:ui-monospace,monospace}
.md-code code{background:transparent;padding:0;color:inherit;font-size:inherit;border-radius:0}
.md-quote{margin:14px 0;padding:10px 14px;border-left:3px solid ${p};background:rgba(124,107,217,.06);color:#4a4859;border-radius:0 8px 8px 0}
.md-quote .md-p{margin:0}
.md-list{margin:10px 0;padding-left:22px}.md-list li{margin:4px 0}
.md-list li .md-p{margin:0}
.md-hr{border:none;border-top:1px solid #ececf2;margin:22px 0}
.md-body a{color:${p};text-decoration:none;border-bottom:1px solid rgba(124,107,217,.3)}
.md-body a:hover{border-bottom-color:${p}}
.md-body strong{font-weight:700;color:#1f1d2e}
.md-task{list-style:none;margin-left:-22px;display:flex;align-items:flex-start;gap:8px}
.md-checkbox{width:16px;height:16px;border:1.5px solid #c7c5d2;border-radius:4px;margin-top:4px;flex-shrink:0;display:inline-block}
.md-checkbox.is-on{background:${m};border-color:${m};position:relative}
.md-checkbox.is-on::after{content:"";position:absolute;left:4px;top:1px;width:4px;height:8px;border:solid white;border-width:0 2px 2px 0;transform:rotate(45deg)}
.md-table-wrap{overflow-x:auto;margin:16px 0;border-radius:10px;border:1px solid #ececf2}
.md-table{width:100%;border-collapse:collapse;font-size:14px;min-width:400px}
.md-table th{background:#f5f4fb;font-weight:600;color:#3a3849;padding:10px 14px;text-align:left;border-bottom:2px solid #e5e4f0;white-space:nowrap}
.md-table td{padding:9px 14px;border-bottom:1px solid #f0eff5;color:#2a2a3a;vertical-align:top}
.md-table tr:last-child td{border-bottom:none}
.md-table tbody tr:hover td{background:#faf9ff}
.hljs-comment,.hljs-quote{color:#676e95;font-style:italic}
.hljs-keyword,.hljs-selector-tag{color:#c792ea}
.hljs-string,.hljs-number,.hljs-literal,.hljs-regexp{color:#c3e88d}
.hljs-title,.hljs-title.class_,.hljs-section{color:#82aaff}
.hljs-attr,.hljs-attribute{color:#ffcb6b}
.hljs-built_in,.hljs-type{color:#f78c6c}
.hljs-variable,.hljs-property,.hljs-template-variable{color:#89ddff}
.hljs-meta,.hljs-meta .hljs-keyword{color:#80cbc4}
.hljs-deletion{color:#ff5370}
.hljs-addition{color:#c3e88d}
.hljs-emphasis{font-style:italic}
.hljs-strong{font-weight:bold}
.md-body del{text-decoration:line-through;color:#9896a8}
`;
